import { EnhancedModel } from '../__generated/configurator-builder.js'
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js'
import { BaseTypes, Finishes } from '../__generated/model/index.js'
import type { PricingConfig } from '../data/pricing-config.js'
import { getReededVariant } from '../data/reeded-data.js'
import { getRogersSingleVariant } from '../data/rogers-data.js'
import {
  FINISH_MAP,
  METAL_FINISH_COLOR_HEX,
  METAL_TEXTURE_URLS,
  WOOD_SPECIES_TEXTURE_URLS,
  getMetalMaterialProps,
  getWoodMaterialProps,
} from '../data/texture-data.js'

export const baseExpressions = implementCustomExpressions({
  Base: {
    $customProductBaseDescription: s => {
      const baseData = s.$baseDataMap[s.type as keyof typeof s.$baseDataMap]
      const finish = FINISH_MAP[s.finish as keyof typeof FINISH_MAP]

      let description = `<h3>Base</h3>\n<ul>`
      description += `<li><strong>Style:</strong> ${baseData.label} (${baseData.baseType})</li>`
      description += `<li><strong>Finish:</strong> ${finish.label}</li>`

      // Add special features
      const features: string[] = []
      if (s.hasCrossbrace) {
        features.push('With Cross Brace')
      }
      if (features.length > 0) {
        description += `<li><strong>Features:</strong> ${features.join(', ')}</li>`
      }

      description += `</ul>`
      return description
    },

    $height: s => s.$defaultHeight + (EnhancedModel.$TraditionalFlushBase.is(s) ? s.$table.$top.thickness + 0.04 : 0),

    $price: s => {
      const p = s.$table.$pricingConfig as PricingConfig
      const basePrice = p.bases[s.type] ?? 0
      const hasThirdBase =
        EnhancedModel.$BaseDoubleLeg.is(s) && s.$table.$top.$length > s.$table.$top.$minTopLengthForExtraBase
      return hasThirdBase ? basePrice * 1.5 : basePrice
    },

    $widthIndicatorVisible: s => !(EnhancedModel.$BaseWithPlatform.is(s) || EnhancedModel.$BaseFourLeg.is(s)),
    $widthIndicatorStartX: s => -s.$width / 2,
    $widthIndicatorEndX: s => s.$width / 2,

    $finishColor: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        return METAL_FINISH_COLOR_HEX
      }
      return FINISH_MAP[s.finish]?.colorHex ?? '#FFFFFF'
    },
    $textureUrl: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        return METAL_TEXTURE_URLS.baseColor
      }
      const species = s.$table.woodSpecies
      return WOOD_SPECIES_TEXTURE_URLS[species]?.baseColor
    },
    $normalMapUrl: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        return METAL_TEXTURE_URLS.normal
      }
      const species = s.$table.woodSpecies
      return WOOD_SPECIES_TEXTURE_URLS[species]?.normal
    },
    $roughnessMapUrl: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        return METAL_TEXTURE_URLS.roughness
      }
      const species = s.$table.woodSpecies
      return WOOD_SPECIES_TEXTURE_URLS[species]?.roughness
    },
    $aoMapUrl: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        return METAL_TEXTURE_URLS.ambientOcclusion
      }
      const species = s.$table.woodSpecies
      return WOOD_SPECIES_TEXTURE_URLS[species]?.ambientOcclusion
    },
    $repeatX: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        // For metal, use metal repeat ratio
        return s.$width / s.$metalRepeatRatio
      }
      // For wood, use wood repeat ratio from top (derived from per-texture size in cm)
      return s.$width / s.$table.$woodTextureSize
    },
    $repeatY: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) {
        // For metal, use the table top length since bases are vertical supports
        return s.$height / s.$metalRepeatRatio
      }
      // For wood, use wood repeat ratio from top (derived from per-texture size in cm)
      return s.$height / s.$table.$woodTextureSize
    },
    $fileUrl: s => s.$table.$glbFileUrl,
    $supportsCrossBrace: s => s.$baseDataMap[s.type as keyof typeof s.$baseDataMap].supportsCrossBrace,

    $objectName: s => {
      if (EnhancedModel.$ReededBase.is(s)) {
        return getReededVariant(s.$table.$top.$width).objectName
      }
      if (EnhancedModel.$RogersSinglePlatformBase.is(s)) {
        return getRogersSingleVariant(s.$table.$top.$width, EnhancedModel.$TopWithWidthAndLength.is(s.$table.$top))
          .objectName
      }
      return s.$baseDataMap[s.type].objectName
    },

    // ----------------------------------Material Properties----------------------------------
    $materialRoughness: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().roughness
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness
    },
    $materialMetalness: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().metalness
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness
    },
    $materialEnvMapIntensity: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().envMapIntensity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity
    },
    $materialSpecularIntensity: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().specularIntensity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity
    },
    $materialNormalScale: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().normalScale
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale
    },
    $materialReflectivity: s => {
      const baseType = s.$baseDataMap[s.type].baseType
      if (baseType === BaseTypes.Metal) return getMetalMaterialProps().reflectivity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity
    },
  },
})

export const baseInvariants = implementCustomInvariants({
  Base: {
    customBaseFinishNeedsPersonalizedQuote: s => s.finish !== Finishes.Custom,
    baseFourLegMustHaveAprons: s =>
      EnhancedModel.$BaseFourLeg.is(s) ? EnhancedModel.$ApronBasic.is(s.$table.$top.$apron) : true,
    supportsCrossbrace: s => s.$supportsCrossBrace || !s.hasCrossbrace,
  },
})
